/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { AudioStreamer } from '../lib/audio-streamer';
import { audioContext } from '../lib/utils';
import { base64ToArrayBuffer } from '../lib/utils';
import AudioPulse from '../components/audio-pulse/AudioPulse';
import { useReconnectingWebSocket } from '../hooks/useReconnectingWebSocket';

const SAMPLE_RATE = 16000;
const BUFFER_SIZE = 4096;

interface EnhancedMediaRecorder extends MediaRecorder {
  cleanup?: () => void;
}

function Voice() {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trialError, setTrialError] = useState<string | null>(null);
  const [volume, setVolume] = useState(0);
  const [isLLMSpeaking, setIsLLMSpeaking] = useState(false);
  const mediaRecorderRef = useRef<EnhancedMediaRecorder | null>(null);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>();
  const isRecordingRef = useRef(false);

  const handleWsMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      if (message.error) {
        setTrialError(message.error);
        return;
      }

      if (message.interrupted) {
        setIsLLMSpeaking(false);
        if (audioStreamerRef.current) {
          audioStreamerRef.current.stop();
        }
        return;
      }

      if (message.type === 'audio' && message.data) {
        setIsLLMSpeaking(true);
        const audioData = base64ToArrayBuffer(message.data);
        const audioStreamer = audioStreamerRef.current;

        if (audioStreamer) {
          audioStreamer.addPCM16(new Uint8Array(audioData));

          if (!analyserRef.current && audioStreamer.context) {
            const analyser = audioStreamer.context.createAnalyser();
            analyser.fftSize = 256;
            if (audioStreamer.gainNode) {
              audioStreamer.gainNode.connect(analyser);
              analyserRef.current = analyser;
              updateVolume();
            }
          }
        }
      }

      if (message.turnComplete) {
        if (audioStreamerRef.current) {
          audioStreamerRef.current.complete();
        }
      }
    } catch (err) {
      console.error('Error processing message:', err);
    }
  }, []);

  const { isConnected, isServerReady, isReconnecting, connectionError, wsRef } =
    useReconnectingWebSocket({
      path: '/voice',
      onMessage: handleWsMessage,
    });

  const canRecord = isConnected && isServerReady && !isReconnecting;
  const serverReadyRef = useRef(isServerReady);
  useEffect(() => {
    serverReadyRef.current = isServerReady;
  }, [isServerReady]);

  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: 'audio-out', sampleRate: SAMPLE_RATE }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current.onComplete = () => {
          setIsLLMSpeaking(false);
        };
      });
    }

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const updateVolume = () => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
    setVolume(average / 255);

    animationFrameRef.current = requestAnimationFrame(updateVolume);
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecordingRef.current) {
      if (mediaRecorderRef.current.cleanup) {
        mediaRecorderRef.current.cleanup();
      }
      mediaRecorderRef.current = null;
      isRecordingRef.current = false;
      setIsRecording(false);
      setIsLLMSpeaking(false);
      setVolume(0);

      if (audioStreamerRef.current) {
        audioStreamerRef.current.stop();
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  const startRecording = async () => {
    if (!canRecord) {
      setError('WebSocket is not connected. Please wait or refresh the page.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      audioContextRef.current = new AudioContext({
        sampleRate: SAMPLE_RATE,
        latencyHint: 'interactive',
      });

      const source = audioContextRef.current.createMediaStreamSource(stream);
      const processor = audioContextRef.current.createScriptProcessor(BUFFER_SIZE, 1, 1);
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      processor.onaudioprocess = (e) => {
        const ws = wsRef.current;
        if (ws?.readyState === WebSocket.OPEN && serverReadyRef.current) {
          const inputData = e.inputBuffer.getChannelData(0);
          const pcm16 = new Int16Array(inputData.length);

          for (let i = 0; i < inputData.length; i++) {
            const s = Math.max(-1, Math.min(1, inputData[i]));
            pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }

          ws.send(pcm16.buffer);
        }
      };

      mediaRecorderRef.current = new MediaRecorder(stream) as EnhancedMediaRecorder;
      isRecordingRef.current = true;
      setIsRecording(true);
      setError(null);

      mediaRecorderRef.current.cleanup = () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        processor.disconnect();
        source.disconnect();
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        stream.getTracks().forEach((track) => track.stop());
      };
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to start recording - check microphone permissions');
    }
  };

  const connectionLabel = isReconnecting
    ? 'Reconectando…'
    : isConnected && !isServerReady
      ? 'Conectando al asistente…'
      : isConnected
        ? 'Conectado'
        : 'Desconectado';

  const displayError = error || connectionError;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md overflow-hidden relative">
          {trialError && (
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
              role="dialog"
              aria-modal="true"
              aria-labelledby="voice-trial-error-title"
            >
              <div className="bg-white p-8 rounded-lg shadow-lg max-w-md mx-4 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 id="voice-trial-error-title" className="text-xl font-bold text-gray-900 mb-2">Función No Disponible</h3>
                <p className="text-gray-600 mb-6">
                  Esta función no está disponible. Por favor, contacta al administrador del
                  calendario para activar esta función.
                </p>
              </div>
            </div>
          )}

          <header className="bg-primary text-white p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div
                className={`w-3 h-3 ${canRecord ? 'bg-green-400' : 'bg-yellow-400'} rounded-full`}
                aria-hidden="true"
              ></div>
              <h1 className="text-xl font-bold">Asistente Fácil (Voz)</h1>
              <span className="text-sm opacity-90" aria-hidden="true">
                ({connectionLabel})
              </span>
              <span className="sr-only">{connectionLabel}</span>
            </div>
          </header>

          <div className="flex flex-col items-center justify-center p-8 space-y-8">
            <div className="w-32 h-32 flex items-center justify-center">
              <AudioPulse active={isLLMSpeaking} volume={volume} />
            </div>

            <button
              onClick={isRecording ? stopRecording : startRecording}
              className={`px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 ${
                isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-secondary hover:bg-secondary/90'
              } text-white shadow-lg`}
              disabled={!canRecord && !isRecording}
              aria-pressed={isRecording}
              aria-label={isRecording ? 'Terminar llamada' : 'Iniciar llamada'}
            >
              {isRecording ? 'Terminar Llamada' : 'Iniciar Llamada'}
            </button>

            {displayError && (
              <div className="text-center text-red-600 bg-red-50 p-4 rounded-lg border border-red-200 max-w-md">
                {displayError}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Voice;
